import { useTranslation } from 'react-i18next';
import Text from '../../../components/atoms/text';
import RadioOption from '../../../components/atoms/radio';
import YardService, { IYard } from '../../../services/Yard.service';
import { useCallback, useEffect, useState } from 'react';
import InputCalendar from '../../../components/molecules/input-calendar';
import Select from '../../../components/atoms/select';
import Button from '../../../components/atoms/button';
import Input from '../../../components/atoms/input';
import ReportsService, {
	ContainerReportProps,
} from '../../../services/Reports.service';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import { useRouteLoaderData } from 'react-router-dom';
import { IUser } from '../../../services/User.service';
import { HomeLoaderData } from '../../../types/homeLoaderData';

interface QueryForm {
	type?: '0' | '1' | '2' | '3';
	startDate?: Date;
	endDate?: Date;
	search?: string;
	yard?: string;
}

const initialForm: QueryForm = {
	type: undefined,
	startDate: undefined,
	endDate: undefined,
	search: '',
	yard: undefined,
};

const Queries = () => {
	const { t } = useTranslation();

	const { accountId, user } = useRouteLoaderData('home') as HomeLoaderData;

	const [form, setForm] = useState<QueryForm>(initialForm);
	const [yards, setYards] = useState<IYard[]>([]);
	const [data, setData] = useState<ContainerReportProps[]>([]);

	const loadYards = useCallback(async () => {
		const response = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setYards(response.data);
	}, []);

	useEffect(() => {
		loadYards();
	}, [loadYards]);

	const handleChange = (name: keyof QueryForm, value: any) => {
		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	useEffect(() => {
		setForm((prev) => ({
			...prev,
			yard: undefined,
			search: '',
		}));
	}, [form.type]);

	const handleSearch = async () => {
		setData([]);
		const response = await ReportsService.filteredReports({
			idClient: Number(accountId),
			startDate: form.startDate,
			endDate: form.endDate,
			yard: form.yard,
			container:
				form.search !== '' && form.type === '0'
					? form.search
					: undefined,
			travelCode:
				form.search !== '' && form.type === '1'
					? form.search
					: undefined,
			ship:
				form.search !== '' && form.type === '2'
					? form.search
					: undefined,
			clientEmail:
				form.search !== '' && form.type === '3'
					? form.search
					: undefined,
		});

		let data: ContainerReportProps[] = [];
		if (response) {
			data = response.data;
		}

		if (data && data.length > 0) {
			setData(data);
			handleReports(data);
		} else NotificationToast.Warn(t('queries.no_data'));
	};

	const handleReports = (reportData: ContainerReportProps[]) => {
		sessionStorage.setItem('report', JSON.stringify(reportData));

		const params = new URLSearchParams({
			_t: Date.now().toString(),
		});

		// Navegar para a página de reports com os parâmetros
		const reportUrl = `/reports/data?${params.toString()}`;

		// Abrir em nova aba
		window.open(reportUrl, '_blank');
	};

	return (
		<div className="flex-grow bg-white rounded-lg shadow-md p-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
			<div className="flex flex-col gap-4 flex-1">
				<Text as="h5">{t('queries.select_type')}:</Text>
				<div className="flex gap-4 flex-wrap">
					<RadioOption
						id="queryTypeContainer"
						name="queryType"
						label={t('port_management.container')}
						value="0"
						checked={form.type === '0'}
						onChange={(e) => handleChange('type', e.target.value)}
					/>
					<RadioOption
						id="queryTypeTravel"
						name="queryType"
						label={t('port_management.ships.travel')}
						value="1"
						checked={form.type === '1'}
						onChange={(e) => handleChange('type', e.target.value)}
					/>
					<RadioOption
						id="queryTypeShip"
						name="queryType"
						label={t('port_management.ship')}
						value="2"
						checked={form.type === '2'}
						onChange={(e) => handleChange('type', e.target.value)}
					/>
					{user.type !== 'Client' && (
						<RadioOption
							id="queryTypeClient"
							name="queryType"
							label={t('admin.client')}
							value="3"
							checked={form.type === '3'}
							onChange={(e) => handleChange('type', e.target.value)}
						/>
					)}
				</div>
			</div>
			{(form.type !== undefined || data.length > 0) && (
				<div className="flex flex-col gap-4">
					<Text as="h5">{t('queries.fill_fields')}:</Text>
					<div className="grid xl:grid-cols-3 gap-4">
						<Input
							name="search"
							containerClassName="xl:col-span-3"
							placeholder={t('misc.search')}
							icon="Search"
							value={form.search}
							onChange={(e) =>
								handleChange('search', e.target.value)
							}
						/>
						<Select
							name="language"
							options={yards.map((yard) => yard.name)}
							value={form.yard || ''}
							onChange={(e) =>
								handleChange('yard', e.target.value)
							}
							title={t('port_management.yard')}
							label={t('port_management.yard')}
							placeholder={t('monitor.select_yard')}
						/>
						<InputCalendar
							date={form.startDate}
							onDateChange={(date) =>
								handleChange('startDate', date)
							}
							placeholder={t('date.initial')}
							label={t('date.initial')}
						/>
						<InputCalendar
							date={form.endDate}
							onDateChange={(date) =>
								handleChange('endDate', date)
							}
							placeholder={t('date.final')}
							label={t('date.final')}
						/>
					</div>
					<Button className="w-28" onClick={handleSearch}>
						{t('misc.generate')}
					</Button>
				</div>
			)}
		</div>
	);
};

export default Queries;
